import { Application } from '@/types/application';
import { Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { getUpdatedNodes } from './application';
import { findEntrySubroutine } from './blocks';

export const changeIndex = (array: any[], fromIndex: number, toIndex: number) => {
  const element = array[fromIndex];
  array.splice(fromIndex, 1);
  array.splice(toIndex, 0, element);

  return array;
};

// Get all metadata blocks that have a configuration that includes any of the types included in the inparameter
const getMetadataValues = (metadata: Metadata, type: string[]) => {
  const result: { type: string; value: string }[] = [];
  metadata.blocks.forEach((block) =>
    block.configuration.forEach((config) => {
      if (type.includes(config.type)) {
        result.push({ type: block.type, value: config.value });
      }
    })
  );

  return result;
};

// Filter out subroutine nodes to only keep those matching the type of metadata blocks returned from getMetadataValues.
// Returns the node and the block value of blocks with the correct substart id
const getNodesOfType = (
  metadataBlocks: { type: string; value: string }[],
  application: Application,
  subroutineId: number
) => {
  const result = [] as { node: number; value: string }[];
  application.subroutines.forEach((subroutine) =>
    subroutine.nodes.forEach((node) => {
      if (
        application.blocks.details[node]?.substart === subroutineId ||
        (!application.blocks.details[node]?.substart &&
          subroutine.id === subroutineId &&
          subroutine.nodes.includes(node))
      ) {
        const block = metadataBlocks.find((metaBlock) => metaBlock.type === application?.blocks.nodes[node]?.type);
        block && result.push({ node: node, value: block.value });
      }
    })
  );
  return result;
};

export const getRelevantNodes = (
  application: Application,
  metadata: Metadata,
  subroutineId: number,
  type: string[]
) => {
  return getNodesOfType(getMetadataValues(metadata, type), application, subroutineId);
};

// Every node whose validation reads the subroutine's interface: its own nodes,
// substart callers, event handlers starting it, and appreturn holders when it is the entry subroutine.
export const getDependentNodes = (application: Application, metadata: Metadata, subroutine: Subroutine): string[] => {
  const ownNodes = subroutine.nodes.map((node) => node.toString());
  const callerNodes = getUpdatedNodes(subroutine.id, 'substart', application.blocks.details);

  const handlerNodes = Object.entries(application.blocks.event_handlers ?? {})
    .filter(([, handlers]) => Object.values(handlers).some((handler) => handler.eventstart === subroutine.id))
    .map(([nodeId]) => nodeId);

  const entry = findEntrySubroutine(application.subroutines, application.blocks.nodes, metadata);
  const appReturnNodes =
    entry?.id === subroutine.id ? getAppReturnNodes(application, metadata).map((holder) => holder.node.toString()) : [];

  return [...new Set([...ownNodes, ...callerNodes, ...handlerNodes, ...appReturnNodes])];
};

// An appreturnvalues list binds to the entry subroutine's interface no matter which subroutine holds its block.
export const getAppReturnNodes = (application: Application, metadata: Metadata) => {
  const metadataBlocks = getMetadataValues(metadata, ['appreturnvalues']);
  const result = [] as { node: number; value: string }[];
  Object.entries(application.blocks.nodes).forEach(([nodeId, node]) => {
    const block = metadataBlocks.find((metaBlock) => metaBlock.type === node.type);
    block && result.push({ node: Number.parseInt(nodeId), value: block.value });
  });
  return result;
};
